import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, TextField, LinearProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { THEME, PAIRS } from './genesisData';
import { InfoIcon, InfoIconFor } from '../../components/common/InfoIcon';
import Modal from '../../components/common/Modal';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HandymanIcon from '@mui/icons-material/Handyman';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';

const PAIR_COLOR = Object.fromEntries(PAIRS.map((p) => [p.customer, p.customerColor]));

const DEFAULT_THRESHOLD = 0.85;

// Plain-English explainer copy shown in the info tooltips.
const TIP = {
  overallScore: 'Average score across all suites. Higher means the AI is producing output closer to what we expect.',
  suitesPassing: 'How many of the 4 evaluation suites (Genesis, Capabilities, Tools, Artifacts) scored above the threshold.',
  threshold: 'Minimum score the AI must hit before changes can ship. Below this, the system blocks the deploy.',
  latestRun: 'Results from the most recent harness run. Re-runs whenever a prompt, capability, or tool changes.',
  runHistory: 'Trend over time. Each row is one full harness run, with the score that came out and what triggered it.',
  diagnostic: 'When something fails or scores low, the exact issue shows here so you know what to fix.',
  borderline: 'Passed overall but one or more individual checks fell below threshold. Worth investigating.',
  stages: 'Stage 1 pulls facts from the transcript. Stage 2 computes pricing. Stage 3 writes the deck.',
  triggers:
    'We test each capability with 20 messages that should activate it and 10 that should not. This shows how many were classified correctly.',
  toolChecks: 'Schema Valid: did the tool return data in the expected shape. Latency p95: 95% of calls finished within this time.',
  artifactChecks:
    'Renders: the component loaded without crashing. Props Valid: the data passed in matched the expected shape. Tokens OK: visual style matches our design system.',
  lastFullSweep:
    'When the harness last ran every suite end to end, and what model produced the scores.',
  thresholdGate:
    'Any suite scoring below its threshold blocks the deploy. Tighten the dial to be stricter; loosen to triage.',
};

const SUITE_TIP = {
  genesis:
    'Tests the deck-generation pipeline against 4 known gold-standard customer pairs. Catches prompt drift in any of the 3 stages.',
  capabilities:
    'Tests each capability with 20 trigger phrases and 10 non-triggers. Catches when a capability starts matching the wrong messages or misses its own triggers (drift).',
  tools:
    'Tests every atomic tool the agents call. Validates schema, measures latency p95, and catches latency regressions and never-fail violations.',
  artifacts:
    'Tests every React component the platform renders. Catches mount failures, prop-schema violations, and design-token deviations.',
};

const SUITES = [
  {
    id: 'genesis',
    label: 'Genesis',
    Icon: AutoAwesomeIcon,
    latestRunTime: 'Today at 14:32',
    grid: '2fr 1fr 1fr 1fr 1fr 1fr',
    columns: [
      { header: 'Pair' },
      { header: 'Stage 1', tip: TIP.stages },
      { header: 'Stage 2' },
      { header: 'Stage 3' },
      { header: 'Overall' },
      { header: 'Status' },
    ],
    rows: [
      {
        id: 'instacart',
        cells: [
          { type: 'entity', name: 'Instacart', color: PAIR_COLOR.Instacart, team: 'Social Impact' },
          { type: 'score', value: 0.94 },
          { type: 'score', value: 0.92 },
          { type: 'score', value: 0.88 },
          { type: 'overall', value: 0.91 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'ims',
        cells: [
          { type: 'entity', name: 'IMS', color: PAIR_COLOR.IMS, team: 'Marketing + Travel + HR' },
          { type: 'score', value: 0.96 },
          { type: 'score', value: 0.94 },
          { type: 'score', value: 0.93 },
          { type: 'overall', value: 0.94 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'wbd-csr',
        cells: [
          { type: 'entity', name: 'WBD CSR', color: PAIR_COLOR['WBD CSR'], team: 'CSR' },
          { type: 'score', value: 0.95 },
          { type: 'score', value: 0.91 },
          { type: 'score', value: 0.89 },
          { type: 'overall', value: 0.92 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'wbd-wellbeing',
        cells: [
          { type: 'entity', name: 'WBD Wellbeing', color: PAIR_COLOR['WBD Wellbeing'], team: 'Wellbeing' },
          { type: 'score', value: 0.82 },
          { type: 'score', value: 0.88 },
          { type: 'score', value: 0.84 },
          { type: 'overall', value: 0.85 },
          { type: 'status', status: 'borderline' },
        ],
      },
    ],
    diagnostics: [
      {
        id: 'd1',
        title: 'Stage 1 failures',
        count: 1,
        content:
          "WBD Wellbeing - 3 of 20 trigger messages missed: 'sweet treat tuesday', 'recurring activation', 'office-by-office cadence'",
      },
      { id: 'd2', title: 'Stage 2 anchor mismatches', count: 0, content: 'No mismatches in latest run.' },
      {
        id: 'd3',
        title: 'Stage 3 hallucinated capabilities',
        count: 1,
        content:
          "WBD Wellbeing - 'Last-Mile Delivery' claimed as live in deck draft; registry status is 'planned'. Auto-corrected.",
      },
    ],
    runs: [
      { id: 'r1', when: 'Today 14:32', score: 0.91, trend: 'up', trigger: 'manual' },
      { id: 'r2', when: 'Today 09:15', score: 0.88, trend: 'down', trigger: 'stage2 prompt edit' },
      { id: 'r3', when: 'Yesterday 18:40', score: 0.9, trend: 'up', trigger: 'registry sync' },
      { id: 'r4', when: 'Yesterday 03:00', score: 0.87, trend: 'down', trigger: 'nightly cron' },
      { id: 'r5', when: 'May 27 03:00', score: 0.89, trend: 'up', trigger: 'nightly cron' },
    ],
  },
  {
    id: 'capabilities',
    label: 'Capabilities',
    Icon: PsychologyIcon,
    latestRunTime: 'Today at 14:30',
    grid: '1.8fr 1.1fr 1.4fr 0.9fr 0.9fr',
    columns: [
      { header: 'Capability' },
      { header: 'Triggers Matched', tip: TIP.triggers },
      { header: 'Non-triggers Rejected' },
      { header: 'Score' },
      { header: 'Status' },
    ],
    rows: [
      {
        id: 'travel_coordination',
        cells: [
          { type: 'entity', name: 'travel_coordination' },
          { type: 'text', value: '20/20' },
          { type: 'text', value: '10/10' },
          { type: 'overall', value: 0.96 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'csr_campaign',
        cells: [
          { type: 'entity', name: 'csr_campaign' },
          { type: 'text', value: '19/20' },
          { type: 'text', value: '10/10' },
          { type: 'overall', value: 0.93 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'recurring_delivery',
        cells: [
          { type: 'entity', name: 'recurring_delivery' },
          { type: 'text', value: '20/20' },
          { type: 'text', value: '9/10' },
          { type: 'overall', value: 0.92 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'recognition_program',
        cells: [
          { type: 'entity', name: 'recognition_program' },
          { type: 'text', value: '18/20' },
          { type: 'text', value: '10/10' },
          { type: 'overall', value: 0.9 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'wellbeing_activation',
        cells: [
          { type: 'entity', name: 'wellbeing_activation' },
          { type: 'text', value: '16/20' },
          { type: 'text', value: '8/10' },
          { type: 'overall', value: 0.81 },
          { type: 'status', status: 'fail' },
        ],
      },
      {
        id: 'offsite_planning',
        cells: [
          { type: 'entity', name: 'offsite_planning' },
          { type: 'text', value: '20/20' },
          { type: 'text', value: '10/10' },
          { type: 'overall', value: 0.97 },
          { type: 'status', status: 'pass' },
        ],
      },
    ],
    diagnostics: [
      {
        id: 'd1',
        title: 'Trigger phrase regressions',
        count: 1,
        content:
          "wellbeing_activation - missed 4 of 20 triggers: 'sweet treat tuesday', 'morale boost', 'office snack drop', 'wellness moment'",
      },
      {
        id: 'd2',
        title: 'False-positive matches',
        count: 2,
        content:
          "recurring_delivery activated on 'order me a ride to LAX' (score 0.86, expected ride_booking). csr_campaign activated on 'find me wellness vendors' (score 0.87, expected wellbeing_activation)",
      },
      {
        id: 'd3',
        title: 'Auto-version bumps blocked',
        count: 1,
        content: 'wellbeing_activation v7 saved as draft. Score 0.81 below 0.85 threshold. v6 stays active.',
      },
    ],
    runs: [
      { id: 'r1', when: 'Today 14:30', score: 0.92, trend: 'up', trigger: 'manual' },
      { id: 'r2', when: 'Today 11:15', score: 0.89, trend: 'down', trigger: 'trigger append (csr_campaign)' },
      { id: 'r3', when: 'Yesterday 22:00', score: 0.91, trend: 'up', trigger: 'trigger append (travel_coordination)' },
      { id: 'r4', when: 'Yesterday 03:00', score: 0.88, trend: 'flat', trigger: 'nightly cron' },
      { id: 'r5', when: 'May 27 03:00', score: 0.88, trend: 'down', trigger: 'nightly cron' },
    ],
  },
  {
    id: 'tools',
    label: 'Tools',
    Icon: HandymanIcon,
    latestRunTime: 'Today at 14:25',
    grid: '1.8fr 1.1fr 1.1fr 0.9fr 0.9fr',
    columns: [
      { header: 'Tool' },
      { header: 'Schema Valid', tip: TIP.toolChecks },
      { header: 'Latency p95' },
      { header: 'Score' },
      { header: 'Status' },
    ],
    rows: [
      {
        id: 'eagle_eye_search',
        cells: [
          { type: 'entity', name: 'eagle_eye_search' },
          { type: 'check', ok: true },
          { type: 'text', value: '1.2s' },
          { type: 'overall', value: 0.98 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'payment_issue_card',
        cells: [
          { type: 'entity', name: 'payment_issue_card' },
          { type: 'check', ok: true },
          { type: 'text', value: '0.4s' },
          { type: 'overall', value: 0.99 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'search_flights',
        cells: [
          { type: 'entity', name: 'search_flights' },
          { type: 'check', ok: true },
          { type: 'text', value: '2.1s' },
          { type: 'overall', value: 0.94 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'browser_agent_run',
        cells: [
          { type: 'entity', name: 'browser_agent_run' },
          { type: 'check', ok: true },
          { type: 'text', value: '8.7s' },
          { type: 'overall', value: 0.87 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'monday_sync',
        cells: [
          { type: 'entity', name: 'monday_sync' },
          { type: 'check', ok: true },
          { type: 'text', value: '0.9s' },
          { type: 'overall', value: 0.96 },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'benevity_export',
        cells: [
          { type: 'entity', name: 'benevity_export' },
          { type: 'check', ok: false },
          { type: 'text', value: '1.4s' },
          { type: 'overall', value: 0.74 },
          { type: 'status', status: 'fail' },
        ],
      },
    ],
    diagnostics: [
      {
        id: 'd1',
        title: 'Schema violations',
        count: 1,
        content:
          "benevity_export returned object missing required field 'volunteerHours'. Mock fixture expected {hours: number, employeeId: string}, got {hours: null, employeeId: 'emp_123'}",
      },
      {
        id: 'd2',
        title: 'Latency regressions',
        count: 1,
        content:
          'browser_agent_run p95 latency rose from 6.2s to 8.7s vs last run. Possibly Playwright fallback firing more often.',
      },
      {
        id: 'd3',
        title: 'Never-fail violations',
        count: 0,
        content: 'All tools returned a structured result, no thrown exceptions.',
      },
    ],
    runs: [
      { id: 'r1', when: 'Today 14:25', score: 0.91, trend: 'down', trigger: 'manual' },
      { id: 'r2', when: 'Today 09:00', score: 0.94, trend: 'up', trigger: 'tool registry sync' },
      { id: 'r3', when: 'Yesterday 18:20', score: 0.92, trend: 'up', trigger: 'manual' },
      { id: 'r4', when: 'Yesterday 03:00', score: 0.89, trend: 'down', trigger: 'nightly cron' },
      { id: 'r5', when: 'May 27 03:00', score: 0.93, trend: 'up', trigger: 'nightly cron' },
    ],
  },
  {
    id: 'artifacts',
    label: 'Artifacts',
    Icon: DashboardCustomizeIcon,
    latestRunTime: 'Today at 14:20',
    grid: '2fr 1fr 1fr 1fr 1fr',
    columns: [
      { header: 'Artifact' },
      { header: 'Renders', tip: TIP.artifactChecks },
      { header: 'Props Valid' },
      { header: 'Tokens OK' },
      { header: 'Status' },
    ],
    rows: [
      {
        id: 'recognition_card',
        cells: [
          { type: 'entity', name: 'recognition_card' },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'travel_hub_panel',
        cells: [
          { type: 'entity', name: 'travel_hub_panel' },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'marketing_inbox_card',
        cells: [
          { type: 'entity', name: 'marketing_inbox_card' },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'status', status: 'pass' },
        ],
      },
      {
        id: 'csr_dashboard',
        cells: [
          { type: 'entity', name: 'csr_dashboard' },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'check', ok: false },
          { type: 'status', status: 'borderline' },
        ],
      },
      {
        id: 'venue_negotiation_card',
        cells: [
          { type: 'entity', name: 'venue_negotiation_card' },
          { type: 'check', ok: true },
          { type: 'check', ok: false },
          { type: 'check', ok: true },
          { type: 'status', status: 'fail' },
        ],
      },
      {
        id: 'delivery_status_card',
        cells: [
          { type: 'entity', name: 'delivery_status_card' },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'check', ok: true },
          { type: 'status', status: 'pass' },
        ],
      },
    ],
    diagnostics: [
      { id: 'd1', title: 'Render failures', count: 0, content: 'All 60 artifacts mounted without throwing.' },
      {
        id: 'd2',
        title: 'Schema violations',
        count: 1,
        content:
          "venue_negotiation_card received prop 'deposit_amount' as string, schema expects number. Type coercion blocked at runtime.",
      },
      {
        id: 'd3',
        title: 'Design token deviations',
        count: 1,
        content: 'csr_dashboard used hex #1a1a1a for background, design system token is #1c1c1c. Fix or update token.',
      },
    ],
    runs: [
      { id: 'r1', when: 'Today 14:20', score: 0.94, trend: 'up', trigger: 'manual' },
      { id: 'r2', when: 'Today 08:45', score: 0.92, trend: 'down', trigger: 'artifact registry sync' },
      { id: 'r3', when: 'Yesterday 19:00', score: 0.95, trend: 'up', trigger: 'artifact added (gifting_options)' },
      { id: 'r4', when: 'Yesterday 03:00', score: 0.91, trend: 'down', trigger: 'nightly cron' },
      { id: 'r5', when: 'May 27 03:00', score: 0.93, trend: 'up', trigger: 'nightly cron' },
    ],
  },
];

const STATUS_MAP = {
  pass: { label: 'PASS', bg: THEME.mintDim, color: THEME.mint },
  borderline: { label: 'BORDERLINE', bg: THEME.amberDim, color: THEME.amber },
  fail: { label: 'FAIL', bg: 'rgba(198,61,87,0.15)', color: THEME.pink },
};

const scrollSx = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(18,192,153,0.4)', borderRadius: '10px' },
};

const mintBtnSx = {
  background: THEME.mint,
  color: '#06281f',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  boxShadow: 'none',
  '&:hover': { background: '#0fa884', boxShadow: 'none' },
};

const outlinedBtnSx = {
  color: THEME.textSecondary,
  borderColor: THEME.borderStrong,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  '&:hover': { borderColor: THEME.mint, color: THEME.mint, background: THEME.mintFaint },
};

// ----- helper components -----

const Card = ({ children, sx = {} }) => (
  <Box
    sx={{
      background: THEME.card,
      border: `1px solid ${THEME.border}`,
      borderRadius: '12px',
      p: 2.5,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const StatCard = ({ label, value, valueColor, caption, captionTone, tip }) => (
  <Card sx={{ flex: 1, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: THEME.textMuted }}>
        {label}
      </Typography>
      {tip && <InfoIcon text={tip} />}
    </Box>
    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: valueColor || THEME.textPrimary, my: 0.5, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {caption && (
      <Typography sx={{ fontSize: '11px', color: captionTone === 'mint' ? THEME.mint : THEME.textMuted }}>{caption}</Typography>
    )}
  </Card>
);

const ScoreCell = ({ score, threshold }) => {
  const pass = score >= threshold;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
        {score.toFixed(2)}
      </Typography>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: pass ? THEME.mint : THEME.pink, flexShrink: 0 }} />
    </Box>
  );
};

const OverallCell = ({ value, threshold }) => {
  const pass = value >= threshold;
  return (
    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: pass ? THEME.mint : THEME.pink, fontVariantNumeric: 'tabular-nums' }}>
      {value.toFixed(2)}
    </Typography>
  );
};

const CheckCell = ({ ok }) =>
  ok ? <CheckIcon sx={{ fontSize: 16, color: THEME.mint }} /> : <CloseIcon sx={{ fontSize: 16, color: THEME.pink }} />;

const EntityCell = ({ name, color, team }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    {color && <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />}
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{ fontSize: '13px', fontWeight: 500, color: THEME.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {name}
      </Typography>
      {team && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{team}</Typography>}
    </Box>
  </Box>
);

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.pass;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          background: s.bg,
          color: s.color,
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          px: '8px',
          py: '3px',
          borderRadius: '4px',
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {s.label}
      </Box>
      <InfoIconFor label={s.label} />
    </Box>
  );
};

const renderCell = (cell, key, threshold) => {
  switch (cell.type) {
    case 'entity':
      return <EntityCell key={key} name={cell.name} color={cell.color} team={cell.team} />;
    case 'score':
      return <ScoreCell key={key} score={cell.value} threshold={threshold} />;
    case 'overall':
      return <OverallCell key={key} value={cell.value} threshold={threshold} />;
    case 'check':
      return (
        <Box key={key}>
          <CheckCell ok={cell.ok} />
        </Box>
      );
    case 'text':
      return (
        <Typography key={key} sx={{ fontSize: '13px', color: THEME.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
          {cell.value}
        </Typography>
      );
    case 'status':
      return (
        <Box key={key}>
          <StatusBadge status={cell.status} />
        </Box>
      );
    default:
      return <Box key={key} />;
  }
};

const HistoryRow = ({ run, threshold }) => {
  const pass = run.score >= threshold;
  let TrendIcon = ArrowUpwardIcon;
  let trendColor = THEME.mint;
  if (run.trend === 'down') {
    TrendIcon = ArrowDownwardIcon;
    trendColor = THEME.pink;
  } else if (run.trend === 'flat') {
    TrendIcon = RemoveIcon;
    trendColor = THEME.textMuted;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, borderBottom: `1px solid ${THEME.border}` }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '12px', color: THEME.textPrimary }}>{run.when}</Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            mt: 0.5,
            fontSize: '10px',
            color: THEME.textMuted,
            background: 'rgba(255,255,255,0.05)',
            border: `1px solid ${THEME.border}`,
            borderRadius: '4px',
            px: 0.75,
            py: '2px',
          }}
        >
          {run.trigger}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <TrendIcon sx={{ fontSize: 13, color: trendColor }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: pass ? THEME.mint : THEME.pink, fontVariantNumeric: 'tabular-nums' }}>
          {run.score.toFixed(2)}
        </Typography>
      </Box>
      <Box
        component="button"
        sx={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: THEME.textMuted,
          fontSize: '11px',
          fontWeight: 600,
          p: 0,
          flexShrink: 0,
          '&:hover': { color: THEME.mint },
        }}
      >
        View
      </Box>
    </Box>
  );
};

const DiagnosticAccordion = ({ title, count, content }) => (
  <Accordion
    disableGutters
    sx={{
      background: 'rgba(255,255,255,0.02)',
      border: `1px solid ${THEME.border}`,
      borderRadius: '8px',
      mb: 1,
      boxShadow: 'none',
      color: THEME.textPrimary,
      overflow: 'hidden',
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon sx={{ color: THEME.textMuted }} />}
      sx={{ minHeight: 0, '& .MuiAccordionSummary-content': { my: 1.25 } }}
    >
      <Typography sx={{ fontSize: '13px', color: THEME.textPrimary }}>
        {title}{' '}
        <Box component="span" sx={{ color: count > 0 ? THEME.amber : THEME.mint, fontWeight: 700 }}>
          ({count})
        </Box>
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>{content}</Typography>
    </AccordionDetails>
  </Accordion>
);

const TabButton = ({ label, IconComponent, active, onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      p: '12px 12px',
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      fontSize: '12px',
      fontWeight: active ? 600 : 500,
      color: active ? THEME.textPrimary : THEME.textMuted,
      borderBottom: active ? `2px solid ${THEME.mint}` : '2px solid transparent',
      whiteSpace: 'nowrap',
      '&:hover': { color: THEME.textPrimary },
    }}
  >
    <IconComponent sx={{ fontSize: 12 }} />
    {label}
  </Box>
);

// ----- per-suite view: latest run + diagnostics + history rail -----

const SuiteView = ({ suite, threshold }) => (
  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
    {/* LEFT: Latest Run */}
    <Card sx={{ flex: 1, minWidth: 420 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: THEME.mint }}>
            Latest Run
          </Typography>
          <InfoIcon text={TIP.latestRun} />
        </Box>
        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{suite.latestRunTime}</Typography>
      </Box>

      {/* table header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: suite.grid,
          gap: 1,
          alignItems: 'center',
          pb: 1,
          borderBottom: `1px solid ${THEME.border}`,
        }}
      >
        {suite.columns.map((col) => (
          <Box key={col.header} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: THEME.textFaint }}>
              {col.header}
            </Typography>
            {col.tip && <InfoIcon text={col.tip} />}
          </Box>
        ))}
      </Box>

      {/* table rows */}
      {suite.rows.map((row) => (
        <Box
          key={row.id}
          sx={{
            display: 'grid',
            gridTemplateColumns: suite.grid,
            gap: 1,
            alignItems: 'center',
            py: 1.5,
            borderBottom: `1px solid ${THEME.border}`,
          }}
        >
          {row.cells.map((cell, ci) => renderCell(cell, ci, threshold))}
        </Box>
      ))}

      {/* diagnostic detail */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: THEME.textFaint }}>
            Diagnostic Detail
          </Typography>
          <InfoIcon text={TIP.diagnostic} />
        </Box>
        {suite.diagnostics.map((d) => (
          <DiagnosticAccordion key={d.id} title={d.title} count={d.count} content={d.content} />
        ))}
      </Box>
    </Card>

    {/* RIGHT: Run History */}
    <Card sx={{ width: 340, flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: THEME.textFaint }}>
          Run History
        </Typography>
        <InfoIcon text={TIP.runHistory} />
      </Box>
      <Box>
        {suite.runs.map((run) => (
          <HistoryRow key={run.id} run={run} threshold={threshold} />
        ))}
      </Box>
      <Typography sx={{ fontSize: '11px', color: THEME.textMuted, mt: 1.5 }}>5 of 47 runs shown · view all</Typography>
    </Card>
  </Box>
);

// ----- modals -----

const TOTAL_CASES = 38;
const CASE_INTERVAL_MS = 120;

const RunHarnessModal = ({ open, onClose, suite, onSave }) => {
  const [cases, setCases] = useState([]);
  const [passed, setPassed] = useState(0);
  const [failed, setFailed] = useState(0);
  const [done, setDone] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!open) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      setCases([]); setPassed(0); setFailed(0); setDone(false);
      return undefined;
    }
    let i = 0;
    intervalRef.current = setInterval(() => {
      i += 1;
      // deterministic-ish pass/fail using i (no Math.random for reproducibility)
      const isPass = (i % 13) !== 0 && (i % 19) !== 0;
      setCases((prev) => [...prev, { n: i, status: isPass ? 'pass' : 'fail' }]);
      if (isPass) setPassed((p) => p + 1); else setFailed((p) => p + 1);
      if (i >= TOTAL_CASES) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        setDone(true);
      }
    }, CASE_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [open]);

  const total = cases.length;
  const pct = (total / TOTAL_CASES) * 100;
  const finalScore = total > 0 ? +(passed / total).toFixed(2) : 0;
  const last6 = cases.slice(-6);

  const saveRun = () => {
    if (onSave) onSave({ score: finalScore });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Run Harness: ${suite ? suite.label : ''}`}
      subtitle={done ? 'Run complete' : `Case ${total} of ${TOTAL_CASES}`}
      size="md"
      footer={
        done ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>Close</Button>
            <Button variant="contained" sx={mintBtnSx} onClick={saveRun}>Save run</Button>
          </Box>
        ) : (
          <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>Cancel</Button>
        )
      }
    >
      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 8,
            borderRadius: 4,
            background: 'rgba(255,255,255,0.06)',
            '& .MuiLinearProgress-bar': { background: THEME.mint },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
            Passed {passed} . Failed {failed}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
            Score so far: {total > 0 ? (passed / total).toFixed(2) : '0.00'}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${THEME.border}`, borderRadius: '8px', p: 1.5 }}>
        {last6.length === 0 ? (
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>Streaming results...</Typography>
        ) : (
          last6.map((c) => (
            <Box key={c.n} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
              {c.status === 'pass' ? (
                <CheckIcon sx={{ fontSize: 14, color: THEME.mint }} />
              ) : (
                <CloseIcon sx={{ fontSize: 14, color: THEME.pink }} />
              )}
              <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>case {c.n}</Typography>
            </Box>
          ))
        )}
      </Box>
      {done && (
        <Typography sx={{ fontSize: '13px', color: THEME.mint, fontWeight: 700, mt: 2 }}>
          Run complete. Overall {finalScore.toFixed(2)}.
        </Typography>
      )}
    </Modal>
  );
};

const ThresholdsModal = ({ open, onClose, thresholds, onSave }) => {
  const [draft, setDraft] = useState(thresholds);
  useEffect(() => {
    if (open) setDraft(thresholds);
  }, [open, thresholds]);

  const set = (k, v) => setDraft({ ...draft, [k]: v });

  const save = () => {
    const cleaned = {};
    Object.keys(draft).forEach((k) => {
      const n = Number(draft[k]);
      cleaned[k] = Number.isFinite(n) && n >= 0 && n <= 1 ? n : thresholds[k];
    });
    onSave(cleaned);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure Thresholds"
      subtitle="The deploy gate per suite"
      size="md"
      footer={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>Cancel</Button>
          <Button variant="contained" sx={mintBtnSx} onClick={save}>Save</Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted }}>{TIP.thresholdGate}</Typography>
        <InfoIcon text={TIP.thresholdGate} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Object.keys(draft).map((k) => (
          <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '13px', color: THEME.textPrimary, width: 130, textTransform: 'capitalize' }}>{k}</Typography>
            <TextField
              size="small"
              type="number"
              value={draft[k]}
              onChange={(e) => set(k, e.target.value)}
              inputProps={{ step: 0.01, min: 0, max: 1 }}
              InputProps={{ sx: { fontSize: 13, color: THEME.textPrimary, '& fieldset': { borderColor: THEME.border } } }}
              sx={{ width: 120 }}
            />
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>0.00 - 1.00</Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: '11px', color: THEME.textFaint, mt: 2 }}>
        Dummy only. Values update the displayed gate and pass/fail dots; nothing persists.
      </Typography>
    </Modal>
  );
};

// ----- page -----

const EvalHarnessPage = () => {
  const [activeSuite, setActiveSuite] = useState('genesis');
  const [thresholds, setThresholds] = useState({
    genesis: DEFAULT_THRESHOLD,
    capabilities: DEFAULT_THRESHOLD,
    tools: DEFAULT_THRESHOLD,
    artifacts: DEFAULT_THRESHOLD,
  });
  const [runHarnessOpen, setRunHarnessOpen] = useState(false);
  const [thresholdsOpen, setThresholdsOpen] = useState(false);
  const [suites, setSuites] = useState(SUITES);

  const suite = suites.find((s) => s.id === activeSuite) || suites[0];
  const currentThreshold = thresholds[activeSuite];

  const displayedThreshold = (() => {
    const vals = Object.values(thresholds);
    const uniform = vals.every((v) => v === vals[0]);
    return uniform ? vals[0].toFixed(2) : 'mixed';
  })();

  const handleSaveRun = ({ score }) => {
    setSuites((prev) =>
      prev.map((s) =>
        s.id === activeSuite
          ? {
              ...s,
              runs: [
                {
                  id: `r-just-now-${String(Date.now()).slice(-6)}`,
                  when: 'just now',
                  score,
                  trend: s.runs[0] && score < s.runs[0].score ? 'down' : score === (s.runs[0] && s.runs[0].score) ? 'flat' : 'up',
                  trigger: 'manual (harness run)',
                },
                ...s.runs,
              ].slice(0, 5),
            }
          : s
      )
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        color: THEME.textPrimary,
        fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
      }}
    >
      {/* HEADER BAR */}
      <Box
        sx={{
          borderBottom: `1px solid ${THEME.border}`,
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Eval Harness
          </Typography>
          <Typography variant="caption" sx={{ color: THEME.textMuted }}>
            Quality gate for AskUnited pipeline. {currentThreshold.toFixed(2)} hard threshold blocks deploys.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
          <Button variant="contained" startIcon={<PlayArrowIcon />} sx={mintBtnSx} onClick={() => setRunHarnessOpen(true)}>
            Run Harness
          </Button>
          <Button variant="outlined" startIcon={<TuneIcon />} sx={outlinedBtnSx} onClick={() => setThresholdsOpen(true)}>
            Configure Thresholds
          </Button>
        </Box>
      </Box>

      {/* SCROLLABLE CONTENT */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3, ...scrollSx }}>
        {/* GLOBAL STAT ROW (across all suites) */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatCard label="Overall Score" value="0.90" valueColor={THEME.mint} caption="↑ 0.02 vs last run" captionTone="mint" tip={TIP.overallScore} />
          <StatCard label="Suites Passing" value="4 / 4" valueColor={THEME.mint} caption="all above threshold" captionTone="muted" tip={TIP.suitesPassing} />
          <StatCard label="Last Full Sweep" value="2 hours ago" caption="claude-sonnet-4-6" captionTone="muted" tip={TIP.lastFullSweep} />
          <StatCard label="Threshold" value={displayedThreshold} caption="hard block, all suites" captionTone="muted" tip={TIP.threshold} />
        </Box>

        {/* TAB BAR */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderBottom: `1px solid ${THEME.border}`, mb: 3, flexWrap: 'wrap' }}>
          {suites.map((s) => (
            <Box key={s.id} sx={{ display: 'flex', alignItems: 'center' }}>
              <TabButton label={s.label} IconComponent={s.Icon} active={activeSuite === s.id} onClick={() => setActiveSuite(s.id)} />
              <InfoIcon text={SUITE_TIP[s.id]} />
            </Box>
          ))}
        </Box>

        {/* TAB CONTENT */}
        <SuiteView suite={suite} threshold={currentThreshold} />
      </Box>

      <RunHarnessModal
        open={runHarnessOpen}
        onClose={() => setRunHarnessOpen(false)}
        suite={suite}
        onSave={handleSaveRun}
      />
      <ThresholdsModal
        open={thresholdsOpen}
        onClose={() => setThresholdsOpen(false)}
        thresholds={thresholds}
        onSave={setThresholds}
      />
    </Box>
  );
};

export default EvalHarnessPage;
